import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, TemplateRef, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbCollapseModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, forkJoin } from 'rxjs';
import { LoaderService, MotivationalMessage, MotivationalMessageCustomSet, MotMsgCategory, ToastService } from '../../core';
import { MotMessagesData } from '../../models';
import { MessageService } from '../../services';
import { BaseComponent, ConfirmationComponent } from '../../shared';


/**
 * Component for patient Educational and Motivational Messages.
 */
@Component({
	selector: 'tsp-motivational-messages',
	imports: [
		FormsModule,
		CommonModule,
		NgbCollapseModule
	],
	providers: [
		NgbActiveModal
	],
	templateUrl: './motivational-messages.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class MotivationalMessagesComponent extends BaseComponent {

	private readonly modalSrv = inject(NgbModal);
	private readonly msgSrv = inject(MessageService);
	private readonly toastSrv = inject(ToastService);
	private readonly loaderSrv = inject(LoaderService);

	data = input<MotMessagesData>();
	isEnabled = model<boolean>(false);
	selectedSet = model<string | null>(null);
	enableSet = model<number[] | undefined>();
	isValid = output<boolean>();

	patientUser = signal<string | undefined>(undefined);

	isStrutAdjustVisible = false;
	isPinSiteCareVisible = false;
	isMotMsgVisible = false;
	isPhysTherapyVisible = false;

	motMessages = signal<MsgItem[]>([]);
	strutAdjusts = computed(() => this._getSet(this.motMessages(), MotMsgCategory.StrutsAdjustment));
	pinSiteCares = computed(() => this._getSet(this.motMessages(), MotMsgCategory.PinSiteCare));
	generalMsgs = computed(() => this._getSet(this.motMessages(), MotMsgCategory.Generic));
	physTherapies = computed(() => this._getSet(this.motMessages(), MotMsgCategory.PhysicalTherapy));

	customSet = signal<MotivationalMessageCustomSet[]>([]);
	previousSet: string | null = null;

	isRename = false;
	setName = model<string | null>(null);
	readonly SETNAME_MAXLENGTH = 50;


	private readonly init = effect(() => {
		if (this.data()) {
			untracked(() => {
				this.patientUser.set(this.data()!.userId);
				this.isEnabled.set(this.data()!.enabled);
				this.selectedSet.set(this.data()!.customSet ?? null);
				this.previousSet = this.selectedSet();
				forkJoin([this.msgSrv.getSet(), this.msgSrv.getCustomSet(this.patientUser()!)]).subscribe(([msgList, setList]) => {
					this.motMessages.set(msgList?.map(msg => ({ ...msg })) ?? []);
					this.customSet.set(setList);
					this._initEnableSet();
				});
			});
		}
	});

	private _initEnableSet(): void {
		if (this.selectedSet()) {
			this._updateMotMessages();
		} else {
			const selectedIds = this.data()!.enableSet;
			this.motMessages.update(set => set.map(m => selectedIds?.includes(m.id) ? ({ ...m, checked: true }) : ({ ...m, checked: false })));
			this.updateEnableSet();
		}
	}

	private readonly checkIfValid = effect(() => this.isValid.emit(!this.isEnabled() || !!this.enableSet()?.length));

	/**
	 * Toggle switch.
	 */
	toggle(): void {
		this.isEnabled.update(val => !val);
	}

	/**
	 * Set all items of a categorized set.
	 */
	checkAll(list: MsgItem[], event: Event): void {
		const val = (event.target as HTMLInputElement).checked;
		list.forEach(s => s.checked = val);
		this.updateEnableSet();
	}

	/**
	 * Check if all categorized items are checked.
	 */
	isAllChecked(list: MsgItem[]): boolean {
		return list?.every(m => m.checked);
	}

	/**
	 * Update enable set.
	 */
	updateEnableSet(): void {
		const idsList = this.motMessages()?.filter(m => m.checked).map(m => m.id) ?? [];
		this.enableSet.set(idsList);
	}

	/**
	 * Open modal to change selected custom set.
	 */
	openCustomSetModal(): void {
		this._openConfirmModal(this.labels().MOT_MESSAGES_COMPONENT_CONFIRM_MESSAGE, this._updateMotMessages, () => this.selectedSet.set(this.previousSet));
	}

	/**
	 * Update motivational messages of the selected custom set.
	 */
	save(): void {
		this._save(this.selectedSet()!);
	}

	/**
	 * Open modal to create/rename custom set.
	 */
	openModal(modalContent: TemplateRef<any>, isRename = false) {
		this._clearModal();
		this.isRename = isRename;
		this.setName.set(isRename ? this.selectedSet() : null);
		this.modalSrv.open(modalContent, { centered: true, backdrop: 'static', windowClass: 'modal-form' }).result.then(() => {
			const newName = this.setName()!;
			if (isRename) this._rename(newName); else this._save(newName);
			this._clearModal();
		}, () => this._clearModal()
		);
	}

	/**
	 * Open modal to delete selected custom set.
	 */
	openDeleteModal(): void {
		this._openConfirmModal(this.labels().MOT_MESSAGES_COMPONENT_DELETE_MESSAGE, this._delete);
	}

	private _getSet(set: MotivationalMessage[], category: MotMsgCategory): MsgItem[] {
		return set?.filter(msg => msg.category === category) ?? [];
	}

	private _openConfirmModal(msg: string, confirmFunc: Function, cancelFunc: Function = () => { }): void {
		const modal = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modal.componentInstance as ConfirmationComponent).config = {
			title: this.labels().MOT_MESSAGES_COMPONENT_CONFIRM_TITLE,
			message: msg,
			confirmBtn: this.labels().MOT_MESSAGES_COMPONENT_CONFIRM_BTN
		};
		modal.result.then(() => confirmFunc.call(this), () => cancelFunc.call(this));
	}

	private _updateMotMessages(): void {
		const selectedIds = this.customSet().find(s => s.name === this.selectedSet())?.enabledSet;
		this.motMessages.update(set => set.map(m => selectedIds?.includes(m.id) ? ({ ...m, checked: true }) : ({ ...m, checked: false })));
		this.previousSet = this.selectedSet();
		this.updateEnableSet();
	}

	private _clearModal(): void {
		this.isRename = false;
		this.setName.set(null);
	}

	private _save(name: string): void {
		const motMessageIds = this.motMessages().filter(m => m.checked).map(m => m.id);
		this.loaderSrv.show();
		this.msgSrv.saveCustomSet(name, motMessageIds).pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.labels().MOT_MESSAGES_COMPONENT_SAVE_SUCCESS);
				this._updateCustomSet(name);
			},
			error: () => this.toastSrv.showError(this.labels().MOT_MESSAGES_COMPONENT_SAVE_ERROR)
		});
	}

	private _updateCustomSet(name: string | null): void {
		this.msgSrv.getCustomSet(this.patientUser()!).subscribe(list => {
			this.customSet.set(list ?? []);
			this.selectedSet.set(name);
			this.previousSet = this.selectedSet();
			// if (!name) this._updateMotMessages();
			this._updateMotMessages();
		});
	}

	private _rename(newName: string): void {
		this.loaderSrv.show();
		this.msgSrv.renameCustomSet(this.selectedSet()!, newName).pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.labels().MOT_MESSAGES_COMPONENT_RENAME_SUCCESS);
				this._updateCustomSet(newName);
			},
			error: () => this.toastSrv.showError(this.labels().MOT_MESSAGES_COMPONENT_RENAME_ERROR)
		});
	}

	private _delete(): void {
		this.loaderSrv.show();
		this.msgSrv.deleteCustomSet(this.selectedSet()!).pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.labels().MOT_MESSAGES_COMPONENT_DELETE_SUCCESS);
				this._updateCustomSet(null);
			},
			error: () => this.toastSrv.showError(this.labels().MOT_MESSAGES_COMPONENT_DELETE_ERROR)
		});
	}

}

export interface MsgItem extends MotivationalMessage {
	checked?: boolean;
}
